import { usePageRuntime } from '@Pages/pageRuntime/pageRuntimeContextValue';
import { createApi } from '@Pages/pageRuntime/sdk/api';
import { toApiError } from '@Pages/pageRuntime/sdk/apiErrors';
import type {
  SdkScheduleConflict,
  SdkScheduleConflictsHandle,
  SdkScheduleConflictsOptions
} from '@Pages/pageRuntime/sdk/types';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

type ScheduleConflictsResponse = { conflicts: SdkScheduleConflict[]; truncated: boolean };

const NO_CONFLICTS: SdkScheduleConflict[] = [];

/** Runtime implementation of `Sdk.hooks.useScheduleConflicts`. */
export function useScheduleConflicts({
  resourceId,
  ids,
  bookable,
  enabled = true
}: SdkScheduleConflictsOptions): SdkScheduleConflictsHandle {
  const sdk = usePageRuntime()?.Sdk;
  // The read is for signed-in staff; a guest would only get a 401.
  const signedIn = (sdk?.identity ?? null) !== null;
  const canRead = enabled && signedIn && resourceId.length > 0;
  const body = {
    ...(ids !== undefined ? { ids } : {}),
    ...(bookable !== undefined
      ? {
          bookable: {
            resource_id: bookable.resourceId,
            ids: bookable.ids
          }
        }
      : {})
  };
  const query = useQuery<ScheduleConflictsResponse, ReturnType<typeof toApiError>>({
    queryKey: ['schedule-conflicts', resourceId, body],
    enabled: canRead,
    retry: false,
    refetchOnMount: 'always',
    staleTime: 0,
    throwOnError: false,
    queryFn: async ({ signal }) =>
      createApi(axios).post<ScheduleConflictsResponse>(
        `/api/data/resources/${encodeURIComponent(resourceId)}/schedule-conflicts`,
        body,
        { signal }
      )
  });
  const isLoading = canRead && query.data === undefined && (query.isFetching || query.isPaused);
  const read = canRead && !query.isError ? query.data : undefined;

  return {
    conflicts: read?.conflicts ?? NO_CONFLICTS,
    truncated: read?.truncated ?? false,
    isLoading,
    error: canRead ? query.error : null,
    refetch: async () => {
      if (canRead) {
        await query.refetch();
      }
    }
  };
}
